import { useCallback, useEffect, useMemo, useState } from "react";
import type { Refs, Row, TabDef } from "../types";
import { api, ApiError } from "../api";
import { formatCell, statusTone } from "../format";
import RecordForm from "./RecordForm";
import ErrorState from "./ErrorState";
import { IconSearch, IconPlus, IconEdit, IconTrash, IconRefresh } from "../icons";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome } from "../ordem";

interface Props {
  tab: TabDef;
  refs: Refs;
  onDataChanged: () => void; // avisa o App para recarregar referências/dashboard
}

export default function CrudView({ tab, refs, onDataChanged }: Props) {
  // Excluir é só para administrador (regra do Portal): sem isso, o botão some.
  const podeExcluir = usePodeExcluir();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  // Aba espelhada do RH/DP (Equipe): estado do botão "Sincronizar".
  const [sincronizando, setSincronizando] = useState(false);
  const [avisoSync, setAvisoSync] = useState<{ ok: boolean; texto: string } | null>(null);
  /**
   * Quem o RH/DP tem mas NÃO é equipe de campo (cozinheira, costureira,
   * vendedor, administrativo). Guardado e escondido: jogar 60 nomes desses no
   * aviso de sucesso fazia parecer que eles tinham ENTRADO neste sistema,
   * quando o certo é o contrário — eles ficaram de fora.
   */
  const [foraDaEquipe, setForaDaEquipe] = useState<{ nome: string; motivo: string }[]>([]);
  const [verForaDaEquipe, setVerForaDaEquipe] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const achados = !q
      ? rows
      : rows.filter((r) =>
          tab.fields.some((f) => {
            const value = f.ref
              ? f.ref === "obras"
                ? r.obra_nome
                : r.colaborador_nome
              : r[f.name];
            return String(value ?? "").toLowerCase().includes(q);
          }),
        );
    // Cadastro identificado por nome (Equipe, Equipamentos, Veículos pela
    // placa): a tabela aparece em ordem alfabética A → Z. Aba de lançamento não
    // define `ordenarPor` e continua na ordem do servidor (o mais novo primeiro).
    const campo = tab.ordenarPor;
    return campo ? ordenarPorNome(achados, (r) => r[campo]) : achados;
  }, [rows, search, tab.fields, tab.ordenarPor]);

  /** Quem ficou de fora da equipe, também em ordem alfabética. */
  const foraDaEquipeOrdenado = useMemo(
    () => ordenarPorNome(foraDaEquipe, (p) => p.nome),
    [foraDaEquipe],
  );

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      await api.create(tab.resource, data);
    }
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  /** Puxa o cadastro do RH/DP para esta base (aba espelhada). */
  async function handleSincronizar() {
    setSincronizando(true);
    setAvisoSync(null);
    try {
      const r = await api.sincronizarColaboradores();
      setAvisoSync({ ok: r.ok, texto: r.mensagem });
      setForaDaEquipe(r.naoSaoDeCampo ?? []);
      setVerForaDaEquipe(false);
      await load();
      onDataChanged();
    } catch (err) {
      setAvisoSync({
        ok: false,
        texto: err instanceof Error ? err.message : "Não foi possível sincronizar com o RH/DP.",
      });
    } finally {
      setSincronizando(false);
    }
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtered.length} registro(s)</span>
          {tab.fonteRh ? (
            // Cadastro centralizado no RH/DP: aqui não se cria colaborador,
            // só se traz o que já foi cadastrado lá.
            <button className="btn-primary" onClick={handleSincronizar} disabled={sincronizando}>
              <IconRefresh size={17} /> {sincronizando ? "Sincronizando..." : "Sincronizar com RH/DP"}
            </button>
          ) : (
            <button className="btn-primary" onClick={openNew}>
              <IconPlus size={17} /> Novo
            </button>
          )}
        </div>
      </div>

      {tab.fonteRh && (
        <div className="crud-aviso">
          O cadastro de colaboradores — inclusive as{" "}
          <strong>competências</strong> — é feito no <strong>RH</strong> ou no{" "}
          <strong>DP</strong> e desce automaticamente para cá. Esta lista é só consulta.
        </div>
      )}

      {avisoSync && (
        <div className={`crud-aviso${avisoSync.ok ? "" : " crud-aviso-erro"}`}>
          {avisoSync.texto}
          {foraDaEquipe.length > 0 && (
            <button
              type="button"
              className="crud-aviso-link"
              onClick={() => setVerForaDaEquipe((v) => !v)}
            >
              {verForaDaEquipe ? "Ocultar" : `Ver quem ficou de fora (${foraDaEquipe.length})`}
            </button>
          )}
          {verForaDaEquipe && (
            <div className="crud-fora">
              <p>
                Estas pessoas estão cadastradas no RH/DP mas <strong>não entram neste sistema</strong>,
                porque o cargo delas não é de equipe de campo. Para trazer alguém para cá, corrija o
                cargo (ou o departamento) no cadastro do RH/DP e sincronize de novo.
              </p>
              <ul>
                {foraDaEquipeOrdenado.map((p) => (
                  <li key={`${p.nome}-${p.motivo}`}>
                    <strong>{p.nome}</strong> — {p.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className={`table${tab.compact ? " table-compact" : ""}`}>
            <thead>
              <tr>
                {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  {tab.fields.filter((f) => !f.hideInTable).map((f) => {
                    const text = formatCell(f, row);
                    return (
                      <td key={f.name}>
                        {f.type === "status" && text !== "—" ? (
                          <span className={`pill pill-${statusTone(text)}`}>{text}</span>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                  <td className="col-actions">
                    <button
                      className="icon-btn"
                      title={tab.fonteRh ? "Ver dados do colaborador (mantidos pelo RH/DP)" : "Editar"}
                      onClick={() => openEdit(row)}
                    >
                      <IconEdit size={17} />
                    </button>
                    {/* Excluir colaborador é do RH/DP — aqui é só espelho.
                        E excluir qualquer registro é exclusivo de administrador. */}
                    {!tab.fonteRh && podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir"
                        onClick={() => handleDelete(row)}
                      >
                        <IconTrash size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <RecordForm
          title={
            tab.fonteRh
              ? `Colaborador — ${tab.label}`
              : editing
                ? `Editar — ${tab.label}`
                : `Novo — ${tab.label}`
          }
          fields={tab.fields}
          initial={editing}
          refs={refs}
          bloquearOrigemRh={tab.fonteRh}
          somenteLeitura={tab.fonteRh}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
